import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { takeUntil, map } from 'rxjs/operators';
import { UserPreferences } from '../../../../shared/models/user-preferences.model';
import { UtilsService } from '../../../../shared/services/utils.service';
import { trackEventAction } from '../../../../shared/store/actions/shared.action';
import * as fromShared from '../../../../shared/store/selectors/shared.selector';
import { User, UserAddon } from '../../../models/user.model';
import { loadPaymentEstimateAction } from '../../../store/actions/payment.action';
import * as fromPayment from '../../../store/selectors/payment.selector';
import { OfferToggleComponent } from '../../offer-toggle/offer-toggle.component';
import { combineLatest, Subject } from 'rxjs';
import { AvailableAddons, PaymentEstimateV3 } from '../../../models/payment.model';
import { CurrencyDecimalPipe } from '../../../../shared/pipes/currency-decimal.pipe';
import { CheckoutDialogComponent } from '../checkout-dialog/checkout-dialog.component';
import * as fromAuth from '../../../../authentication/store/selectors/authentication.selector';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: `app-addons-dialog`,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatButtonModule,
    OfferToggleComponent,
    CurrencyDecimalPipe,
    MatTooltipModule,
  ],
  templateUrl: `./addons-dialog.component.html`,
  styleUrl: `./addons-dialog.component.scss`,
})
export class AddonsDialogComponent implements OnInit, OnDestroy {
  addon: UserAddon;
  fiscalYear: number;
  defaultFiscalYear: number = moment().year();
  paymentEstimate: PaymentEstimateV3;
  availableAddons: AvailableAddons;

  userPreferences: UserPreferences;
  stablecoins: string[];
  fiats: string[];
  user: User;
  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly authStore$: Store<fromAuth.State>,
    private readonly sharedStore$: Store<fromShared.State>,
    private readonly paymentStore$: Store<fromPayment.State>,
    private readonly utilsService: UtilsService,
    private readonly dialogRef: MatDialogRef<AddonsDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    private readonly data: {
      addon?: UserAddon;
      fiscalYear?: number;
      upgradeFishingName?: string;
    },
  ) {}

  ngOnInit(): void {
    if (!this.utilsService.isFiscalYearSwitchable(this.defaultFiscalYear)) {
      this.defaultFiscalYear -= 1;
    }

    this.fiscalYear = this.data?.fiscalYear || this.defaultFiscalYear;

    this.authStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromAuth.selectUser),
        map((user: User) => {
          this.user = user;
        }),
      )
      .subscribe();

    this.paymentStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromPayment.selectPaymentEstimate),
        map((paymentEstimate: PaymentEstimateV3) => {
          this.paymentEstimate = paymentEstimate;
        }),
      )
      .subscribe();

    combineLatest([
      this.sharedStore$.select(fromShared.selectStableCoins),
      this.sharedStore$.select(fromShared.selectFiats),
      this.sharedStore$.select(fromShared.selectUserPreferences),
    ])
      .pipe(
        takeUntil(this.destroy$),
        map(([stablecoins, fiats, userPreferences]) => {
          this.stablecoins = stablecoins;
          this.fiats = fiats;
          this.userPreferences = userPreferences;
        }),
      )
      .subscribe();

    this.paymentStore$
      .pipe(
        takeUntil(this.destroy$),
        select(fromPayment.selectAvailableAddons),
        map((availableAddons: AvailableAddons) => {
          this.availableAddons = availableAddons;

          if (this.availableAddons) {
            this.toggleAddon(this.data.addon ?? `AOCARE001`);
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleAddon(addon: UserAddon): void {
    this.addon = addon;

    if (
      (this.addon === `AOCARE001` && this.availableAddons?.AOCARE001) ||
      (this.addon === `AOCARE002` && this.availableAddons?.AOCARE002)
    ) {
      this.getEstimate();
    }
  }

  getEstimate(): void {
    this.paymentStore$.dispatch(
      loadPaymentEstimateAction({
        fiscalYear: this.fiscalYear,
        checkoutType: `ADDON`,
        requestedPlan: null,
        requestedAddOn: this.addon,
      }),
    );
  }

  bookCall(): void {
    const data: any = {
      addon_type: this.addon === `AOCARE001` ? `Expert Review` : `Full Service`,
    };

    if (this.data.upgradeFishingName) {
      data.upgrade_fishing = this.data.upgradeFishingName;
    }

    this.sharedStore$.dispatch(
      trackEventAction({
        event: `call_full_service_profile`,
        data,
      }),
    );

    let url = ``;
    switch (this.userPreferences?.language) {
      case `fr`:
        url = `https://meet.brevo.com/waltio/full-service`;
        break;
      case `en`:
        url = `https://meet.brevo.com/waltio/full-service-en`;
        break;
      case `es`:
        url = `https://meet.brevo.com/waltio/full-service-es`;
        break;
    }

    window.open(url, `_blank`);

    this.dialogRef.close();
  }

  openCheckoutDialog(): void {
    if (this.user.lockedToApple) {
      this.utilsService.openLockedMobileToast();
    } else {
      this.utilsService.openDialog(CheckoutDialogComponent, `478px`, `auto`, {
        checkoutType: `ADDON`,
      });
    }

    this.dialogRef.close();
  }
}
